/**
 * PATH: packages/ui/src/hooks/useBreakpoint.ts
 *
 * Reactive Tailwind breakpoint helpers for mobile-aware components.
 *
 * Stack 11 PR 3 — extracted from the inline ``isSmUp/isLgUp``
 * block in ``useLayoutState.ts:32-50`` so EmailHub, EmailHubV2,
 * KanbanV2, and any future page can stop hand-rolling
 * ``window.matchMedia`` (current count: ``KanbanBoardView.tsx:165``,
 * ``KanbanEmailAllocationRail.tsx:110``, ``KanbanInboxRail.tsx:185``,
 * ``apps/sam/src/features/kanban/KanbanPage.tsx``, plus the four
 * inline blocks across ``apps/newmark``).
 *
 * Why a hook instead of a CSS-only solution?
 *   We need to make JS decisions (e.g. "auto-collapse the rail
 *   below 1024px", "switch from split-pane to single-pane
 *   layout") that aren't expressible in pure Tailwind classes.
 *   Tailwind handles the visual hide/show; this hook handles
 *   the behaviour decisions.
 *
 * SSR-safety: every initial-state probe checks ``typeof window``
 * so Server-Side-Rendered hosts (Next.js, Vite SSR) don't crash
 * on import. The default for SSR is ``true`` (assume desktop) —
 * the matchMedia subscription fires immediately on mount and
 * corrects to the real breakpoint within one render cycle.
 *
 * Tailwind defaults (mirrored here):
 *   sm: 640px
 *   md: 768px
 *   lg: 1024px
 *   xl: 1280px
 *   2xl: 1536px
 *
 * If a future PR overrides Tailwind breakpoints, this file MUST
 * be kept in sync — there's no run-time discovery from the
 * tailwind.config.js (Vite doesn't expose it to the bundle).
 */
import { useEffect, useState } from 'react'

const SM = 640
const MD = 768
const LG = 1024
const XL = 1280

function probe(minWidth: number): boolean {
  if (typeof window === 'undefined') return true
  return window.matchMedia(`(min-width: ${minWidth}px)`).matches
}

function useMinWidth(minWidth: number): boolean {
  const [matches, setMatches] = useState<boolean>(() => probe(minWidth))
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`)
    const update = () => setMatches(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [minWidth])
  return matches
}

/** True when viewport ≥ 640px (Tailwind ``sm:``). */
export function useIsSmUp(): boolean {
  return useMinWidth(SM)
}

/** True when viewport ≥ 768px (Tailwind ``md:``). */
export function useIsMdUp(): boolean {
  return useMinWidth(MD)
}

/** True when viewport ≥ 1024px (Tailwind ``lg:``). */
export function useIsLgUp(): boolean {
  return useMinWidth(LG)
}

/** True when viewport ≥ 1280px (Tailwind ``xl:``). */
export function useIsXlUp(): boolean {
  return useMinWidth(XL)
}

/** Inverse of ``useIsLgUp`` — true on phones / small tablets (< 1024px). */
export function useIsMobile(): boolean {
  return !useIsLgUp()
}

/** True on phones (< 768px) — drives the dedicated calendar mobile layout
 *  (horizontal date strip + day/agenda) which auto-engages below ``md:``,
 *  while 768–1023px tablets keep the desktop grid + drawer sidebar. */
export function useIsPhone(): boolean {
  return !useIsMdUp()
}

/** Returns the current named breakpoint. */
export function useBreakpoint(): 'xs' | 'sm' | 'md' | 'lg' | 'xl' {
  const sm = useIsSmUp()
  const md = useIsMdUp()
  const lg = useIsLgUp()
  const xl = useIsXlUp()
  if (xl) return 'xl'
  if (lg) return 'lg'
  if (md) return 'md'
  if (sm) return 'sm'
  return 'xs'
}
